import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Plus, MapPin, Shirt, Key, Trash2, Edit2, X, Save,
  Mountain, Users, Coins, BookMarked, Shield, Flame,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import { SplitViewTrigger } from '../components/SplitViewLayout';
import Sidebar from '../components/Sidebar';
import { useProject } from '../context/ProjectContext';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tab definitions — idKey drives endpoint path and document ID field
const TAB_CONFIG = {
  locations:     { label: 'Locations',     icon: MapPin,      idKey: 'location_id',  endpoint: 'locations' },
  costumes:      { label: 'Costumes',      icon: Shirt,       idKey: 'costume_id',   endpoint: 'costumes' },
  props:         { label: 'Props',         icon: Key,         idKey: 'prop_id',      endpoint: 'props' },
  world_physical:{ label: 'Physical World',icon: Mountain,    idKey: 'physical_id',  endpoint: 'world_physical' },
  world_society: { label: 'Society',       icon: Users,       idKey: 'society_id',   endpoint: 'world_society' },
  world_economy: { label: 'Economy',       icon: Coins,       idKey: 'economy_id',   endpoint: 'world_economy' },
  world_history: { label: 'History',       icon: BookMarked,  idKey: 'history_id',   endpoint: 'world_history' },
  world_rules:   { label: 'Rules & Tech',  icon: Shield,      idKey: 'rules_id',     endpoint: 'world_rules' },
  world_conflict:{ label: 'Conflict',      icon: Flame,       idKey: 'conflict_id',  endpoint: 'world_conflict' },
};

const EMPTY_DATA = Object.fromEntries(Object.keys(TAB_CONFIG).map(k => [k, []]));

export default function WorldBuilding({ embeddedProjectId, splitProps } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { project } = useProject();

  // embeddedProjectId: passed when rendered inline (e.g. Series Builder)
  // otherwise fall back to workspace context
  const effectiveProjectId = embeddedProjectId || project?.plotProjectId || null;
  const isScoped = Boolean(effectiveProjectId);
  const isEmbedded = Boolean(embeddedProjectId);
  const isInWorkspace = !isEmbedded && Boolean(project?.plotProjectId);
  const workspaceBack = isInWorkspace ? `/project/${project.plotProjectId}` : null;

  const [user, setUser] = useState(null);
  const [data, setData] = useState(EMPTY_DATA);
  const [projects, setProjects] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('locations');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const worldUrl = effectiveProjectId
        ? `${API}/worldbuilding?project_id=${effectiveProjectId}`
        : `${API}/worldbuilding`;
      const [userRes, worldRes, projectsRes] = await Promise.all([
        authFetch(`${API}/auth/me`, { credentials: 'include' }),
        authFetch(worldUrl, { credentials: 'include' }),
        authFetch(`${API}/projects`, { credentials: 'include' }),
      ]);
      setUser(await userRes.json());
      const worldData = await worldRes.json();
      // Merge fetched data with EMPTY_DATA so all 9 keys always exist
      setData({ ...EMPTY_DATA, ...worldData });
      const projectsData = await projectsRes.json();
      setProjects(Array.isArray(projectsData) ? projectsData : []);
      setCharacters(Array.isArray(projectsData) ? projectsData.filter(p => p.project_type === 'character') : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load world building data');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData(effectiveProjectId ? { project_id: effectiveProjectId } : {});
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.project_id) { toast.error('Please select a project'); return; }
    if (!formData.name?.trim()) { toast.error('Please enter a name'); return; }

    const cfg = TAB_CONFIG[activeTab];
    try {
      const endpoint = editingItem
        ? `${API}/worldbuilding/${cfg.endpoint}/${editingItem[cfg.idKey]}`
        : `${API}/worldbuilding/${cfg.endpoint}`;
      const response = await authFetch(endpoint, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (response.status === 403) {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Upgrade to the Indie plan for unlimited items');
        return;
      }
      if (!response.ok) throw new Error('Save failed');
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    const cfg = TAB_CONFIG[activeTab];
    try {
      const response = await authFetch(`${API}/worldbuilding/${cfg.endpoint}/${itemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Delete failed');
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getCharacterName = (charId) => {
    const char = characters.find(c => c.project_id === charId);
    return char?.level_0?.name || char?.title || 'Unknown';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto" />
          <p className="mt-4 font-mono">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const activeItems = isScoped
    ? (data[activeTab] || []).filter(item => item.project_id === effectiveProjectId)
    : (data[activeTab] || []);

  const cfg = TAB_CONFIG[activeTab];

  const mainContent = (
    <>
          {/* Tab bar — horizontal scroll for 9 tabs */}
          <div className="flex items-end gap-0 mb-8 border-b border-border/40 overflow-x-auto">
            {Object.entries(TAB_CONFIG).map(([tabKey, tabCfg]) => {
              const Icon = tabCfg.icon;
              return (
                <button
                  key={tabKey}
                  onClick={() => setActiveTab(tabKey)}
                  data-testid={`tab-${tabKey}`}
                  className={`flex items-center gap-2 px-4 py-3 font-mono text-xs uppercase tracking-wider transition-all border-b-2 -mb-px whitespace-nowrap flex-shrink-0
                    ${activeTab === tabKey
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tabCfg.label}
                </button>
              );
            })}
          </div>

          {/* Add Button */}
          <div className="flex justify-end mb-6">
            <Button
              onClick={openAddModal}
              data-testid="add-item-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {cfg.label.replace(/s$/, '')}
            </Button>
          </div>

          {isInWorkspace && (
            <p className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-4">
              Showing items for: <span className="text-primary">{project.projectTitle}</span>
            </p>
          )}

          {/* Items Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeItems.length === 0 ? (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                No {cfg.label.toLowerCase()} yet. Click "Add" to create one.
              </div>
            ) : (
              activeItems.map((item) => (
                <div
                  key={item[cfg.idKey]}
                  className="bg-card border border-border/40 p-6 hover:border-primary/50 transition-colors"
                  data-testid={`world-item-${item[cfg.idKey]}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-mono text-lg font-semibold">{item.name}</h3>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(item)} className="h-8 w-8 p-0">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDelete(item[cfg.idKey])}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {item.project_name && (
                    <div className="inline-block bg-primary/10 border border-primary/20 px-2 py-0.5 mb-3">
                      <span className="text-[10px] font-mono uppercase text-primary tracking-wider">{item.project_name}</span>
                    </div>
                  )}

                  <ItemPreview item={item} tabKey={activeTab} getCharacterName={getCharacterName} />
                </div>
              ))
            )}
          </div>
    </>
  );

  const modal = showModal && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="bg-card border border-border p-8 max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-mono text-xl font-semibold">
            {editingItem ? 'Edit' : 'Add'} {cfg.label.replace(/s$/, '')}
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="space-y-4">
          {/* Project Dropdown — hidden when scoped to a single project */}
          {!isScoped && (
            <div>
              <label className="block font-mono text-sm uppercase mb-2">
                Project <span className="text-destructive">*</span>
              </label>
              <select
                value={formData.project_id || ''}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                data-testid="project-select"
                className="w-full bg-background border border-input rounded-none font-mono px-3 py-2"
              >
                <option value="">Select project</option>
                {projects.map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.level_0?.name || p.title} ({p.project_type})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Name (required for all) */}
          <div>
            <label className="block font-mono text-sm uppercase mb-2">
              {t('name')} <span className="text-destructive">*</span>
            </label>
            <Input
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={`${cfg.label.replace(/s$/, '')} name`}
              data-testid="item-name-input"
              className="rounded-none font-mono"
            />
          </div>

          {/* Tab-specific fields */}
          <ModalFieldCtx.Provider value={{ formData, setFormData }}>
            <ModalFields tab={activeTab} formData={formData} setFormData={setFormData} characters={characters} t={t} />
          </ModalFieldCtx.Provider>
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <Button
            variant="outline"
            onClick={() => setShowModal(false)}
            className="rounded-none font-mono uppercase tracking-widest text-xs"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            data-testid="save-item-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs"
          >
            <Save className="w-4 h-4 mr-2" />
            {t('save')}
          </Button>
        </div>
      </div>
    </div>
  );

  // Embedded mode — render inline without Sidebar/header shell
  if (isEmbedded) {
    return (
      <div className="w-full">
        <main className="w-full py-6">
          {/* Module header — matches the other workspace tabs (h2 + SplitViewTrigger) */}
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-mono text-3xl font-semibold tracking-tight mb-2">{t('worldBuilding')}</h2>
              <p className="text-muted-foreground">Locations, costumes, props, and the rules of your world.</p>
            </div>
            {splitProps && <SplitViewTrigger {...splitProps} />}
          </div>
          {mainContent}
        </main>
        {modal}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <div className="noise-overlay" />
      <Sidebar
        userTier={user?.subscription_tier || 'spark'}
        projectId={isInWorkspace ? project.plotProjectId : undefined}
        projectTitle={isInWorkspace ? project.projectTitle : undefined}
      />
      <div className="flex-1 flex flex-col">
        <header className="glassmorphism sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(workspaceBack || '/dashboard?type=character')}
              className="rounded-none font-mono uppercase tracking-widest text-xs"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {isInWorkspace ? 'Back to Workspace' : t('dashboard')}
            </Button>
            <h1 className="font-mono text-2xl font-bold tracking-tighter">{t('worldBuilding')}</h1>
          </div>
        </header>
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
          {mainContent}
        </main>
        {modal}
      </div>
    </div>
  );
}

/* ── Card preview by tab ──────────────────────────────────────────────────── */
function ItemPreview({ item, tabKey, getCharacterName }) {
  switch (tabKey) {
    case 'locations':
      return (
        <>
          {item.description && <p className="text-sm text-muted-foreground mb-2">{item.description}</p>}
          {item.vibe && <p className="text-xs text-primary italic">Vibe: {item.vibe}</p>}
          {item.plot_significance && <p className="text-xs text-muted-foreground mt-1">Plot: {item.plot_significance}</p>}
        </>
      );
    case 'costumes':
      return (
        <>
          {item.character_id && <p className="text-xs text-primary mb-2">Character: {getCharacterName(item.character_id)}</p>}
          {item.aesthetic && <p className="text-sm text-muted-foreground mb-1">Aesthetic: {item.aesthetic}</p>}
          {item.evolution && <p className="text-xs text-muted-foreground">Evolution: {item.evolution}</p>}
        </>
      );
    case 'props':
      return (
        <>
          {item.owner_id && <p className="text-xs text-primary mb-2">Owner: {getCharacterName(item.owner_id)}</p>}
          {item.symbolic_value && <p className="text-sm text-muted-foreground mb-1">Symbol: {item.symbolic_value}</p>}
          {item.function && <p className="text-xs text-muted-foreground">Function: {item.function}</p>}
        </>
      );
    case 'world_physical':
      return <FieldPreviews item={item} keys={['region_name','geography','climate','architecture_style']} labels={['Region','Geography','Climate','Architecture']} />;
    case 'world_society':
      return <FieldPreviews item={item} keys={['class_structure','customs_and_rituals','belief_system']} labels={['Class','Customs','Belief']} />;
    case 'world_economy':
      return <FieldPreviews item={item} keys={['currency_or_trade','who_controls_resources','how_people_survive']} labels={['Currency','Control','Survival']} />;
    case 'world_history':
      return <FieldPreviews item={item} keys={['the_worlds_wound','founding_myth','propaganda_vs_truth']} labels={['The Wound','Myth','Truth vs Propaganda']} />;
    case 'world_rules':
      return <FieldPreviews item={item} keys={['what_is_possible','what_is_forbidden','technology_level']} labels={['Possible','Forbidden','Tech']} />;
    case 'world_conflict':
      return <FieldPreviews item={item} keys={['whats_broken_in_this_world','who_benefits_from_the_status_quo','the_ticking_clock']} labels={["What's Broken",'Status Quo Beneficiary','Ticking Clock']} />;
    default:
      return null;
  }
}

function FieldPreviews({ item, keys, labels }) {
  return (
    <>
      {keys.map((key, i) => item[key] ? (
        <p key={key} className="text-xs text-muted-foreground mb-1">
          <span className="text-primary/70 uppercase tracking-wide">{labels[i]}:</span> {item[key].length > 80 ? item[key].slice(0, 80) + '…' : item[key]}
        </p>
      ) : null)}
    </>
  );
}

/* ── Modal field (MODULE-LEVEL — never recreated) ───────────────────────────
 * Field used to be defined inside ModalFields. Any render of the host
 * (WorldBuilding standalone OR embedded in Series Builder) gave <Field> a
 * fresh function identity, so React remounted the <textarea> on every
 * keystroke and focus died after ~2 chars. Defining it at module scope makes
 * its identity permanently constant; form state flows in through context so
 * call sites stay unchanged.
 */
const ModalFieldCtx = createContext({ formData: {}, setFormData: () => {} });

function Field({ label, fieldKey, placeholder, multiline }) {
  const { formData, setFormData } = useContext(ModalFieldCtx);
  return (
    <div>
      <label className="block font-mono text-sm uppercase mb-2">{label}</label>
      <AutoExpandTextarea
        value={formData[fieldKey] || ''}
        onChange={(e) => setFormData(prev => ({ ...prev, [fieldKey]: e.target.value }))}
        placeholder={placeholder}
        className={`rounded-none font-mono ${multiline ? 'min-h-[80px]' : 'min-h-[60px]'}`}
      />
    </div>
  );
}

/* ── Modal fields by tab ──────────────────────────────────────────────────── */
function ModalFields({ tab, formData, setFormData, characters, t }) {
  const set = (key) => (e) => setFormData(prev => ({ ...prev, [key]: e.target.value }));

  switch (tab) {
    case 'locations':
      return (
        <>
          <Field label={t('description')} fieldKey="description" placeholder="Describe this location..." multiline />
          <Field label={t('vibe')} fieldKey="vibe" placeholder="Atmosphere, mood, feeling..." />
          <Field label={t('plotSignificance')} fieldKey="plot_significance" placeholder="Why is this location important to the story?" multiline />
        </>
      );
    case 'costumes':
      return (
        <>
          <div>
            <label className="block font-mono text-sm uppercase mb-2">Character</label>
            <select
              value={formData.character_id || ''}
              onChange={set('character_id')}
              data-testid="character-select"
              className="w-full bg-background border border-input rounded-none font-mono px-3 py-2"
            >
              <option value="">Select character</option>
              {characters.map(c => (
                <option key={c.project_id} value={c.project_id}>{c.level_0?.name || c.title}</option>
              ))}
            </select>
          </div>
          <Field label={t('aesthetic')} fieldKey="aesthetic" placeholder="Visual style, colors, materials..." multiline />
          <Field label={t('evolution')} fieldKey="evolution" placeholder="How does the costume change throughout the story?" multiline />
        </>
      );
    case 'props':
      return (
        <>
          <div>
            <label className="block font-mono text-sm uppercase mb-2">{t('owner')}</label>
            <select
              value={formData.owner_id || ''}
              onChange={set('owner_id')}
              data-testid="owner-select"
              className="w-full bg-background border border-input rounded-none font-mono px-3 py-2"
            >
              <option value="">Select owner</option>
              {characters.map(c => (
                <option key={c.project_id} value={c.project_id}>{c.level_0?.name || c.title}</option>
              ))}
            </select>
          </div>
          <Field label={t('symbolicValue')} fieldKey="symbolic_value" placeholder="What does this prop represent thematically?" multiline />
          <Field label={t('function')} fieldKey="function" placeholder="What role does this prop play in the plot?" multiline />
        </>
      );
    case 'world_physical':
      return (
        <>
          <Field label="Region Name" fieldKey="region_name" placeholder="Name or designation for this region" />
          <Field label="Geography" fieldKey="geography" placeholder="Terrain, landscape, physical features..." multiline />
          <Field label="Climate" fieldKey="climate" placeholder="Weather, seasons, atmosphere..." />
          <Field label="Architecture Style" fieldKey="architecture_style" placeholder="How are structures built and arranged?" />
          <Field label="Sensory Details" fieldKey="sensory_details" placeholder="What does it look, sound, and smell like?" multiline />
          <Field label="Accessibility" fieldKey="accessibility" placeholder="Who can go here — and who cannot?" multiline />
        </>
      );
    case 'world_society':
      return (
        <>
          <Field label="Class Structure" fieldKey="class_structure" placeholder="How is society stratified?" multiline />
          <Field label="Customs & Rituals" fieldKey="customs_and_rituals" placeholder="Cultural practices, ceremonies, traditions..." multiline />
          <Field label="Belief System" fieldKey="belief_system" placeholder="Religion, spirituality, philosophy..." multiline />
          <Field label="Political Structure" fieldKey="political_structure" placeholder="How is power organised and exercised?" multiline />
          <Field label="Gender & Family Norms" fieldKey="gender_and_family_norms" placeholder="Roles, expectations, family units..." multiline />
          <Field label="Taboos" fieldKey="taboos" placeholder="What is forbidden or deeply shameful?" multiline />
        </>
      );
    case 'world_economy':
      return (
        <>
          <Field label="Currency / Trade" fieldKey="currency_or_trade" placeholder="What is used for exchange?" />
          <Field label="Who Controls Resources" fieldKey="who_controls_resources" placeholder="Who holds economic power?" multiline />
          <Field label="How People Survive" fieldKey="how_people_survive" placeholder="Day-to-day economic reality for most people..." multiline />
          <Field label="Wealth Distribution" fieldKey="wealth_distribution" placeholder="How equal or unequal is distribution?" multiline />
          <Field label="Black Markets / Underground" fieldKey="black_markets_or_underground" placeholder="What is traded outside official channels?" multiline />
        </>
      );
    case 'world_history':
      return (
        <>
          <Field label="The World's Wound" fieldKey="the_worlds_wound" placeholder="The defining past trauma that shaped this world..." multiline />
          <Field label="Founding Myth" fieldKey="founding_myth" placeholder="The story people tell about how this world began..." multiline />
          <Field label="Propaganda vs Truth" fieldKey="propaganda_vs_truth" placeholder="Official narrative vs what actually happened..." multiline />
          <Field label="Historical Figures" fieldKey="historical_figures" placeholder="People from the past who still cast a shadow..." multiline />
          <Field label="What Is Forbidden to Remember" fieldKey="what_is_forbidden_to_remember" placeholder="History that is buried, denied, or erased..." multiline />
        </>
      );
    case 'world_rules':
      return (
        <>
          <Field label="What Is Possible" fieldKey="what_is_possible" placeholder="What can people do in this world that they can't elsewhere?" multiline />
          <Field label="What Is Forbidden" fieldKey="what_is_forbidden" placeholder="Laws, limits, hard lines..." multiline />
          <Field label="Technology Level" fieldKey="technology_level" placeholder="What technology exists and who has access to it?" multiline />
          <Field label="Magic / Special Systems" fieldKey="magic_or_special_systems" placeholder="If applicable — rules, costs, who wields it..." multiline />
          <Field label="Who Enforces the Rules" fieldKey="who_enforces_the_rules" placeholder="Police, guilds, gods, social pressure..." multiline />
        </>
      );
    case 'world_conflict':
      return (
        <>
          <Field label="What's Broken in This World" fieldKey="whats_broken_in_this_world" placeholder="The core injustice, imbalance, or dysfunction..." multiline />
          <Field label="Who Benefits from the Status Quo" fieldKey="who_benefits_from_the_status_quo" placeholder="Who has a stake in keeping things as they are?" multiline />
          <Field label="Who Wants Change" fieldKey="who_wants_change" placeholder="Who is pushing back — and why?" multiline />
          <Field label="The Ticking Clock" fieldKey="the_ticking_clock" placeholder="What will happen if nothing changes?" multiline />
          <Field label="How the World Will Be Different at the End" fieldKey="how_the_story_world_will_be_different_at_the_end" placeholder="What does the story leave behind in the world?" multiline />
        </>
      );
    default:
      return null;
  }
}
